import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchDevices } from "../../../api/device";
import { EnumDeviceType, enumsectionToI18nKey } from "../../../generated/enums";
import { AllDataDevice } from "../../../generated/types";
import { useAccountStorage } from "../../../hooks/useAccountStorage";
import { useAdvancedSearch } from "../../../hooks/useAdvancedSearch";
import { useDeviceConnections } from "../../../hooks/useDeviceConnections";
import { CSVField, downloadCSV } from "../../../utils/csv";
import { isAdminAccount, remoteDeviceRoute } from "../../../utils/roles";
import {
  clampPage,
  Pagination,
  TABLE_PAGE_SIZE,
} from "../../common/Pagination";
import { compareText, DataTableSort, nextSort } from "../../common/DataTable";
import { EmptyState } from "../../common/EmptyState";
import { TableFilters } from "../../common/TableFilters";
import { Header } from "../../Header";
import { PageTour } from "../../tour/PageTour";
import { devicesTourSteps } from "../../tour/steps/pageSteps";
import { sectionToIcon } from "../../type/default";
import { AssociateDeviceModal } from "./AssociateDeviceModal";
import { DeviceMobileCard } from "./DeviceMobileCard";
import { DevicesSearchHelpModal } from "./DevicesSearchHelpModal";
import { EntityCardSkeletons } from "../../common/EntityCard";
import { MobileSort, sortOptions } from "../../common/MobileSort";
import {
  DEVICE_SORT_VALUES,
  DeviceColumnKey,
  useDeviceColumns,
} from "./DeviceRow";
import { DevicesTableContent } from "./DevicesTableContent";
import { DeviceTicket } from "./DeviceTicket";
import { DeviceTypeFilter } from "./DeviceTypeFilter";
import { LinkDeviceModal } from "./LinkDeviceModal";
import { TICKET_SIDE, TicketColumn } from "../../common/TableDetails";

export const DevicesTable = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<DataTableSort<DeviceColumnKey>>();
  // Pastille et œil : état VPN réel de chaque box (fichiers status d'OpenVPN via DATA).
  const connections = useDeviceConnections();
  const [filterType, setFilterType] = useState<EnumDeviceType | "">("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSearchHelpOpen, setIsSearchHelpOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<AllDataDevice | null>(
    null
  );
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem("deviceFavorites");
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error("Error reading favorites from storage", e);
      return [];
    }
  });
  const [devices, setDevices] = useState<AllDataDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccountStorage } = useAccountStorage();
  const accountStorage = getAccountStorage();
  const isAdmin = isAdminAccount();
  // Deux parcours d'association, selon le rôle :
  //  - **admin/psc** -> LinkDeviceModal : liste TOUTES les entreprises et tous les comptes
  //    (routes admin) ;
  //  - **tous les autres** -> AssociateDeviceModal, qui s'adapte : un éleveur associe à
  //    SA propre entreprise (pas de sélecteur), un installateur choisit parmi ses clients.
  //
  // Le collaborateur éleveur est exclu : le backend lui répond 403 (le partage au
  // collaborateur éleveur n'est pas encore ouvert).
  //
  // Le backend revérifie tout : ces tests ne font que masquer un bouton inutile.
  const isProvider =
    accountStorage?.type === "installer" ||
    accountStorage?.type === "group" ||
    accountStorage?.type === "installer_assistant" ||
    accountStorage?.type === "group_assistant";
  const isBreeder = accountStorage?.type === "breeder";
  const canLinkDevice = isAdmin || isProvider || isBreeder;

  const loadDevices = async () => {
    const { devices: fetchedDevices, error: fetchError } = await fetchDevices();
    setDevices(fetchedDevices);
    setError(fetchError);
    setIsLoading(false);
  };

  const refreshDevices = async () => {
    loadDevices();

    // Mettre à jour le produit sélectionné si nécessaire
    if (selectedDevice && Array.isArray(devices)) {
      const updated = devices.find(
        (u) => u.device.id === selectedDevice.device.id
      );
      if (updated) {
        setSelectedDevice(updated);
      }
    }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  // Sélectionner automatiquement le device depuis l'URL
  useEffect(() => {
    const deviceUuid = searchParams.get("device");
    if (deviceUuid && devices.length > 0) {
      const deviceToSelect = devices.find((d) => d.device?.uuid === deviceUuid);
      if (deviceToSelect) {
        // Vérifier si c'est un nouveau device à sélectionner
        if (!selectedDevice || selectedDevice.device?.uuid !== deviceUuid) {
          setSelectedDevice(deviceToSelect);
        }
        // Nettoyer le paramètre d'URL après sélection
        setSearchParams({}, { replace: true });
      }
    }
  }, [devices, searchParams, selectedDevice, setSearchParams]);

  // Filtrer d'abord par type de produit
  const filteredByType = devices.filter((device) => {
    if (filterType === "") return true;

    if (!device?.device) {
      return false;
    }

    const matches = device.device.type === filterType;
    return matches;
  });

  // Appliquer ensuite la recherche avancée
  const getSearchableFields = (device: AllDataDevice) => {
    if (!device?.device) return [];

    return [
      device.device?.type || "",
      device.company?.company_name || "",
      device.building?.name || "",
      device.breeding?.name || "",
      device.device?.name || "",
      device.device?.uuid || "",
      device.device?.ip_vpn || "",
      device.device?.ip_local || "",
      device.company?.country || "",
      device.device?.version || "",
      device.device?.status || "",
      device.device?.kernel_version || "",
    ];
  };

  const filteredDevices = useAdvancedSearch({
    searchTerm,
    data: filteredByType,
    fields: getSearchableFields,
  });

  // Les favoris restent en tête ; le tri de colonne s'applique à l'intérieur de chaque groupe.
  const sortedDevices = useMemo(() => {
    const favoritesSet = new Set(favorites);
    const isFav = (d: AllDataDevice) =>
      !!d?.device?.uuid && favoritesSet.has(d.device.uuid);
    const sortValue = sort ? DEVICE_SORT_VALUES[sort.key] : undefined;
    return [...filteredDevices].sort((a, b) => {
      if (isFav(a) !== isFav(b)) return isFav(a) ? -1 : 1;
      if (!sort || !sortValue) return 0;
      const order = compareText(sortValue(a), sortValue(b));
      return sort.direction === "asc" ? order : -order;
    });
  }, [filteredDevices, favorites, sort]);

  const currentPage = clampPage(page, TABLE_PAGE_SIZE, sortedDevices.length);
  const pagedDevices = sortedDevices.slice(
    currentPage * TABLE_PAGE_SIZE,
    (currentPage + 1) * TABLE_PAGE_SIZE
  );

  // Un nouveau critère repart de la première page.
  const changeSearch = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };
  const changeType = (value: EnumDeviceType | "") => {
    setFilterType(value);
    setPage(0);
  };
  const applySort = (next?: DataTableSort<DeviceColumnKey>) => {
    setSort(next);
    setPage(0);
  };
  const mobileSortOptions = sortOptions(useDeviceColumns(!isAdmin));
  const changeSort = (key: DeviceColumnKey) => {
    setSort((current) => nextSort(current, key));
    setPage(0);
  };
  const scrollKey = [
    currentPage,
    searchTerm,
    filterType,
    sort?.key ?? "",
    sort?.direction ?? "",
  ].join("|");

  const handleDownload = () => {
    const fields: CSVField<AllDataDevice>[] = [
      {
        header: t("device-type"),
        key: (data) => data.device.type,
      },
      {
        header: t("device-name"),
        key: (data) => data.device.name || "",
      },
      {
        header: t("device-status"),
        key: (data) => data.device.status || "",
      },
      {
        header: t("company"),
        key: (data) => data.company?.company_name || "",
      },
      {
        header: t("breeding"),
        key: (data) => data.company?.company_name || "",
      },
      {
        header: t("building"),
        key: (data) => data.building?.name || "",
      },
      {
        header: t("device-ip-local"),
        key: (data) => data.device.ip_local || "",
      },
      {
        header: t("device-ip-vpn"),
        key: (data) => data.device.ip_vpn || "",
      },
      {
        header: t("device-kernel-version"),
        key: (data) => data.device.kernel_version || "",
      },
      /*{
        header: "Licenses",
        key: (data) =>
          Array.isArray(data.device.licenses)
            ? data.device.licenses.join(", ")
            : "",
      },*/
    ];

    downloadCSV(filteredDevices, fields, "devices");
  };
  // La vue à distance a UNE route par espace (RoleRoute renverrait un éleveur sur /breeder
  // s'il ouvrait celle de l'admin). `remoteDeviceRoute` renvoie undefined pour les espaces
  // sans route dédiée (installateur, groupement) : on masque alors l'accès.
  const handleViewDevice = (uuid: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    const route = remoteDeviceRoute(uuid, accountStorage?.type);
    if (route) navigate(route);
  };

  const toggleFavorite = (uuid?: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!uuid) return;
    setFavorites((prev) => {
      const isFav = prev.includes(uuid);
      const next = isFav ? prev.filter((id) => id !== uuid) : [...prev, uuid];
      localStorage.setItem("deviceFavorites", JSON.stringify(next));
      return next;
    });
  };

  if (error) {
    return (
      <div className="p-4">
        <Header
          title={t(enumsectionToI18nKey["section_devices"])}
          icon={sectionToIcon.section_devices}
        />
        <p className="mt-6 text-red-500">{error}</p>
      </div>
    );
  }

  // Aucun produit : ce n'est pas une erreur mais l'état normal d'un compte qui vient d'être
  // créé (produits pas encore associés). On garde l'en-tête — donc le bouton d'association —
  // et on remplace seulement le tableau, vide et ses filtres inutiles, par un message.
  if (!isLoading && devices.length === 0) {
    return (
      <div className="p-4">
        {/* Écran d'un compte neuf : le tutoriel y vaut surtout, réduit à ses deux
            premières étapes (filtres et liste absents). */}
        <Header
          id="tour-devices-header"
          title={t(enumsectionToI18nKey["section_devices"])}
          icon={sectionToIcon.section_devices}
          buttonText={canLinkDevice ? t("device-link-title") : undefined}
          onButtonClick={canLinkDevice ? () => setIsModalOpen(true) : undefined}
          help={<PageTour id="devices" steps={devicesTourSteps} />}
        />
        <EmptyState
          icon={sectionToIcon.section_devices}
          title={t("device-empty-title")}
          description={
            canLinkDevice
              ? t("device-empty-description-linkable")
              : t("device-empty-description")
          }
        />

        {!isAdmin ? (
          <AssociateDeviceModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onAssociated={loadDevices}
          />
        ) : (
          <LinkDeviceModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              loadDevices();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="w-screen md:w-full flex overflow-x-clip md:overflow-hidden">
      <div
        className={`w-full ${TICKET_SIDE} ${
          selectedDevice ? "lg:overflow-x-auto" : ""
        }`}
      >
        <div className="p-4">
          <Header
            id="tour-devices-header"
            title={t(enumsectionToI18nKey["section_devices"])}
            icon={sectionToIcon.section_devices}
            buttonText={canLinkDevice ? t("device-link-title") : undefined}
            onButtonClick={
              canLinkDevice ? () => setIsModalOpen(true) : undefined
            }
            help={<PageTour id="devices" steps={devicesTourSteps} />}
          />
          <div className="mt-3 rounded-lg">
            <div id="tour-devices-filters">
              <TableFilters
                filterValue={filterType}
                onFilterChange={(value) => changeType(value as EnumDeviceType)}
                filterOptions={[]}
                searchValue={searchTerm}
                onSearchChange={changeSearch}
                onDownload={handleDownload}
                showResultCount={isAdmin}
                resultCount={sortedDevices.length}
                onHelpClick={() => setIsSearchHelpOpen(true)}
                customFilter={
                  <DeviceTypeFilter value={filterType} onChange={changeType} />
                }
              />
            </div>
            {!isLoading && sortedDevices.length === 0 ? (
              <EmptyState
                icon={sectionToIcon.section_devices}
                title={t("table-no-result")}
              />
            ) : (
              <>
                <div
                  id="tour-devices-list-mobile"
                  className="md:hidden space-y-4"
                >
                  <MobileSort
                    options={mobileSortOptions}
                    sort={sort}
                    onChange={applySort}
                  />
                  {isLoading && <EntityCardSkeletons />}
                  {pagedDevices
                    .filter((d) => !!d?.device)
                    .map((d) => {
                      const uuid = d.device?.uuid;
                      const isFav = !!uuid && favorites.includes(uuid);
                      return (
                        <DeviceMobileCard
                          key={uuid || String(d.device?.id)}
                          data={d}
                          isFavorite={isFav}
                          onToggleFavorite={toggleFavorite(uuid)}
                          // Tous les types ont une connexion à distance, comme sur ordinateur
                          // (avant : Serenity seulement sur mobile).
                          onView={uuid ? handleViewDevice(uuid) : undefined}
                          connection={uuid ? connections[uuid] : undefined}
                          onSelect={() => setSelectedDevice(d)}
                          showUuid={!isAdmin}
                        />
                      );
                    })}
                </div>

                <div id="tour-devices-list" className="hidden md:block">
                  <DevicesTableContent
                    devices={pagedDevices}
                    isLoading={isLoading}
                    favorites={favorites}
                    selectedUuid={selectedDevice?.device.uuid ?? ""}
                    sort={sort}
                    onSort={changeSort}
                    scrollKey={scrollKey}
                    onSelectDevice={setSelectedDevice}
                    onToggleFavorite={toggleFavorite}
                    onViewDevice={handleViewDevice}
                    connections={connections}
                    // Hors espace admin : élevage et bâtiment sont vides (route scopée),
                    // et l'UUID est l'identifiant que ces utilisateurs manipulent.
                    showUuid={!isAdmin}
                  />
                </div>
                {!isLoading && (
                  <Pagination
                    page={currentPage}
                    pageSize={TABLE_PAGE_SIZE}
                    total={sortedDevices.length}
                    onPageChange={setPage}
                    showTotal={isAdmin}
                  />
                )}
              </>
            )}
          </div>

          {!isAdmin ? (
            <AssociateDeviceModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onAssociated={loadDevices}
            />
          ) : (
            <LinkDeviceModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
            />
          )}
        </div>
      </div>

      <TicketColumn open={!!selectedDevice}>
        {selectedDevice && (
          <DeviceTicket
            key={selectedDevice.device.id}
            device={selectedDevice.device}
            onClose={() => setSelectedDevice(null)}
            useBackIcon={false}
            onDataRefresh={refreshDevices}
          />
        )}
      </TicketColumn>
      <DevicesSearchHelpModal
        isOpen={isSearchHelpOpen}
        onClose={() => setIsSearchHelpOpen(false)}
      />
    </div>
  );
};
